import React, { useState, useEffect, useCallback } from 'react'
import {
  BarChart3,
  RefreshCw,
  AlertCircle,
  Play,
  TrendingUp,
  TrendingDown,
  Minus,
  Info,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  RotateCcw
} from 'lucide-react'
import clsx from 'clsx'
import * as adminApi from '../../services/adminApi'
import Toast, { ToastContainer } from '../../components/admin/Toast'
import ConfirmDialog from '../../components/admin/ConfirmDialog'

// Domain configuration with colors
const domainConfig = {
  general: { label: 'General', color: 'bg-gray-500' },
  technical: { label: 'Technical', color: 'bg-blue-500' },
  conversational: { label: 'Conversational', color: 'bg-green-500' },
  legal: { label: 'Legal', color: 'bg-purple-500' },
  medical: { label: 'Medical', color: 'bg-red-500' },
  ecommerce: { label: 'E-commerce', color: 'bg-orange-500' },
  financial: { label: 'Financial', color: 'bg-yellow-600' },
  marketing: { label: 'Marketing', color: 'bg-pink-500' },
}

// Quality score color helper
const getScoreColor = (score) => {
  if (score === null || score === undefined) return 'bg-gray-200 text-gray-500'
  if (score >= 0.8) return 'bg-green-100 text-green-800'
  if (score >= 0.6) return 'bg-lime-100 text-lime-800'
  if (score >= 0.4) return 'bg-yellow-100 text-yellow-800'
  if (score >= 0.2) return 'bg-orange-100 text-orange-800'
  return 'bg-red-100 text-red-800'
}

const getScoreBgColor = (score) => {
  if (score === null || score === undefined) return 'bg-gray-100'
  if (score >= 0.8) return 'bg-green-500'
  if (score >= 0.6) return 'bg-lime-500'
  if (score >= 0.4) return 'bg-yellow-500'
  if (score >= 0.2) return 'bg-orange-500'
  return 'bg-red-500'
}

const formatScore = (score) => {
  if (score === null || score === undefined) return '-'
  return (score * 100).toFixed(0)
}

const QualityMatrixPage = () => {
  // State
  const [status, setStatus] = useState(null)
  const [matrix, setMatrix] = useState(null)
  const [domainCoverage, setDomainCoverage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filter state
  const [selectedDomain, setSelectedDomain] = useState('')
  const [selectedLanguagePair, setSelectedLanguagePair] = useState('')
  const [showHeatmap, setShowHeatmap] = useState(true)

  // Modal states
  const [showEvaluateModal, setShowEvaluateModal] = useState(false)
  const [showAccountDetail, setShowAccountDetail] = useState(null)
  const [isEvaluating, setIsEvaluating] = useState(false)
  const [evaluationProgress, setEvaluationProgress] = useState(null)
  const [showResetConfirm, setShowResetConfirm] = useState({ open: false, account: null })
  const [isResetting, setIsResetting] = useState(false)

  // Toast state
  const [toasts, setToasts] = useState([])

  // Add toast helper
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Fetch status
  const fetchStatus = useCallback(async () => {
    try {
      const data = await adminApi.getQualityStatus()
      setStatus(data)
    } catch (err) {
      console.error('Failed to fetch status:', err)
    }
  }, [])

  // Fetch matrix
  const fetchMatrix = useCallback(async () => {
    try {
      const data = await adminApi.getQualityMatrix()
      setMatrix(data)
    } catch (err) {
      console.error('Failed to fetch matrix:', err)
    }
  }, [])

  // Fetch domain coverage
  const fetchDomainCoverage = useCallback(async () => {
    try {
      const data = await adminApi.getDomainCoverage()
      setDomainCoverage(data)
    } catch (err) {
      console.error('Failed to fetch domain coverage:', err)
    }
  }, [])

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      await Promise.all([fetchStatus(), fetchMatrix(), fetchDomainCoverage()])
    } catch (err) {
      setError('Failed to load quality data')
      addToast('Failed to load quality data', 'error')
    } finally {
      setLoading(false)
    }
  }, [fetchStatus, fetchMatrix, fetchDomainCoverage, addToast])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Get unique accounts from matrix
  const getAccounts = () => {
    if (!matrix?.account_metrics) return []
    const accounts = []
    for (const [accountId, langPairs] of Object.entries(matrix.account_metrics)) {
      // Get account info from first metric
      const firstLangPair = Object.values(langPairs)[0]
      const firstDomain = firstLangPair ? Object.values(firstLangPair)[0] : null
      if (firstDomain) {
        accounts.push({
          id: parseInt(accountId),
          model: firstDomain.account_model || `Account ${accountId}`,
          provider: firstDomain.account_provider || 'Unknown'
        })
      } else {
        accounts.push({
          id: parseInt(accountId),
          model: `Account ${accountId}`,
          provider: 'Unknown'
        })
      }
    }
    return accounts
  }

  // Get unique language pairs from matrix
  const getLanguagePairs = () => {
    if (!matrix?.matrix_summary?.language_pairs) return []
    return matrix.matrix_summary.language_pairs
  }

  // Get all domains
  const getDomains = () => {
    return Object.keys(domainConfig)
  }

  // Get score for account/domain/langpair
  const getScore = (accountId, domain, langPair = null) => {
    if (!matrix?.account_metrics?.[accountId]) return null

    const accountData = matrix.account_metrics[accountId]

    if (langPair) {
      return accountData?.[langPair]?.[domain]?.composite_score ?? null
    }

    // Average across all language pairs for this domain
    let scores = []
    for (const langPairData of Object.values(accountData)) {
      if (langPairData[domain]?.composite_score !== null && langPairData[domain]?.composite_score !== undefined) {
        scores.push(langPairData[domain].composite_score)
      }
    }
    return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null
  }

  // Handle evaluation
  const handleRunEvaluation = async (evalConfig) => {
    try {
      setIsEvaluating(true)
      setEvaluationProgress({ status: 'running', message: 'Starting evaluation...' })

      const result = await adminApi.evaluateQuality(evalConfig)

      setEvaluationProgress({
        status: 'completed',
        ...result
      })

      addToast(`Evaluation completed: ${result.successful_evaluations} successful`, 'success')

      // Refresh data
      fetchData()
    } catch (err) {
      console.error('Evaluation failed:', err)
      setEvaluationProgress({
        status: 'failed',
        message: err.response?.data?.detail || 'Evaluation failed'
      })
      addToast('Evaluation failed', 'error')
    } finally {
      setIsEvaluating(false)
    }
  }

  // Handle reset quality metrics
  const handleResetQuality = async () => {
    const account = showResetConfirm.account
    if (!account) return

    try {
      setIsResetting(true)
      await adminApi.resetAccountQuality(account.id)
      addToast('Quality metrics reset successfully', 'success')
      setShowResetConfirm({ open: false, account: null })
      fetchData()
    } catch (err) {
      console.error('Failed to reset quality:', err)
      addToast('Failed to reset quality metrics', 'error')
    } finally {
      setIsResetting(false)
    }
  }

  // Render loading state
  if (loading && !status) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-600">Loading quality matrix...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="quality-matrix-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Quality Matrix
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Translation quality metrics across accounts, domains, and language pairs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </button>
          <button
            onClick={() => setShowEvaluateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <Play className="w-4 h-4 mr-2" />
            Run Evaluation
          </button>
        </div>
      </div>

      {/* Status Overview */}
      {status && (
        <div className="bg-white rounded-lg shadow p-6" data-testid="status-overview">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Status Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900">{status.total_accounts}</div>
              <div className="text-sm text-gray-500">Total Accounts</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-700">{status.evaluated_accounts}</div>
              <div className="text-sm text-gray-500">Evaluated</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-700">{status.coverage_percentage}%</div>
              <div className="text-sm text-gray-500">Coverage</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-700">{status.recent_evaluations_7_days}</div>
              <div className="text-sm text-gray-500">Recent (7 days)</div>
            </div>
            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-indigo-700">
                {status.quality_summary?.quality_statistics?.mean
                  ? formatScore(status.quality_summary.quality_statistics.mean) + '%'
                  : '-'}
              </div>
              <div className="text-sm text-gray-500">Avg. Quality</div>
            </div>
          </div>
        </div>
      )}

      {/* Domain Coverage */}
      {domainCoverage && (
        <div className="bg-white rounded-lg shadow p-6" data-testid="domain-coverage">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Domain Coverage</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {getDomains().map(domain => {
              const coverage = domainCoverage.domain_coverage?.[domain] || {}
              const avgQuality = coverage.average_quality || 0

              return (
                <div
                  key={domain}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  data-testid={`domain-coverage-${domain}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={clsx(
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white',
                      domainConfig[domain]?.color || 'bg-gray-500'
                    )}>
                      {domainConfig[domain]?.label || domain}
                    </span>
                    <span className={clsx(
                      'text-sm font-medium',
                      coverage.coverage_percentage >= 70 ? 'text-green-600' :
                      coverage.coverage_percentage >= 40 ? 'text-yellow-600' : 'text-red-600'
                    )}>
                      {coverage.coverage_percentage?.toFixed(0) || 0}%
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className={clsx(
                        'h-2 rounded-full transition-all',
                        coverage.coverage_percentage >= 70 ? 'bg-green-500' :
                        coverage.coverage_percentage >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                      )}
                      style={{ width: `${coverage.coverage_percentage || 0}%` }}
                    />
                  </div>

                  <div className="text-xs text-gray-500 space-y-1">
                    <div className="flex justify-between">
                      <span>Evaluated:</span>
                      <span>{coverage.evaluated_accounts || 0}/{coverage.total_accounts || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Avg Quality:</span>
                      <span className={clsx(
                        'font-medium',
                        avgQuality >= 0.6 ? 'text-green-600' :
                        avgQuality >= 0.4 ? 'text-yellow-600' : 'text-red-600'
                      )}>
                        {formatScore(avgQuality)}%
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Recommendations */}
          {domainCoverage.recommendations && domainCoverage.recommendations.length > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-yellow-800">Recommendations</h3>
                  <ul className="mt-2 text-sm text-yellow-700 space-y-1">
                    {domainCoverage.recommendations.slice(0, 3).map((rec, idx) => (
                      <li key={idx}>
                        <span className={clsx(
                          'inline-block px-1.5 py-0.5 rounded text-xs font-medium mr-2',
                          rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                          rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                        )}>
                          {rec.priority}
                        </span>
                        {rec.message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <select
            value={selectedDomain}
            onChange={(e) => setSelectedDomain(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="filter-domain"
          >
            <option value="">All Domains</option>
            {getDomains().map(domain => (
              <option key={domain} value={domain}>{domainConfig[domain]?.label || domain}</option>
            ))}
          </select>

          <select
            value={selectedLanguagePair}
            onChange={(e) => setSelectedLanguagePair(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="filter-language-pair"
          >
            <option value="">All Language Pairs</option>
            {getLanguagePairs().map(lp => (
              <option key={lp} value={lp}>{lp}</option>
            ))}
          </select>

          <div className="flex items-center gap-2 ml-auto">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showHeatmap}
                onChange={(e) => setShowHeatmap(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Heatmap View
            </label>
          </div>
        </div>
      </div>

      {/* Quality Heatmap */}
      {showHeatmap && matrix?.account_metrics && Object.keys(matrix.account_metrics).length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="quality-heatmap">
          <div className="p-4 border-b">
            <h2 className="text-lg font-medium text-gray-900">Quality Heatmap</h2>
            <p className="text-sm text-gray-500">
              Composite quality scores by account and domain
              {selectedLanguagePair && ` (${selectedLanguagePair})`}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                    Account
                  </th>
                  {getDomains()
                    .filter(d => !selectedDomain || d === selectedDomain)
                    .map(domain => (
                      <th
                        key={domain}
                        className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <span className={clsx(
                          'inline-block px-2 py-0.5 rounded text-white',
                          domainConfig[domain]?.color || 'bg-gray-500'
                        )}>
                          {domainConfig[domain]?.label || domain}
                        </span>
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(matrix.account_metrics).map(([accountId, langPairs]) => {
                  // Get account info from the accounts dictionary
                  const accountInfo = matrix.accounts?.[accountId]
                  const accountName = accountInfo?.display_name || `Account ${accountId}`
                  const provider = accountInfo?.provider || 'unknown'
                  const model = accountInfo?.model || ''

                  return (
                    <tr key={accountId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm sticky left-0 bg-white z-10">
                        <button
                          onClick={() => setShowAccountDetail({ id: parseInt(accountId), name: accountName, provider, model })}
                          className="hover:text-indigo-600 hover:underline text-left"
                        >
                          <div className="font-medium text-gray-900">{provider}/{model}</div>
                          <div className="text-xs text-gray-500">Account #{accountId}</div>
                        </button>
                      </td>
                      {getDomains()
                        .filter(d => !selectedDomain || d === selectedDomain)
                        .map(domain => {
                          const score = getScore(accountId, domain, selectedLanguagePair || null)

                          return (
                            <td
                              key={domain}
                              className="px-4 py-3 whitespace-nowrap text-center"
                            >
                              <span
                                className={clsx(
                                  'inline-flex items-center justify-center w-12 h-8 rounded text-sm font-medium',
                                  getScoreColor(score)
                                )}
                                title={score !== null ? `${(score * 100).toFixed(1)}%` : 'Not evaluated'}
                              >
                                {formatScore(score)}
                              </span>
                            </td>
                          )
                        })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="p-4 border-t bg-gray-50">
            <div className="flex items-center gap-4 text-xs">
              <span className="text-gray-500 font-medium">Score Legend:</span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-green-500" /> 80%+
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-lime-500" /> 60-79%
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-yellow-500" /> 40-59%
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-orange-500" /> 20-39%
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-red-500" /> &lt;20%
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-gray-200" /> N/A
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!matrix?.account_metrics || Object.keys(matrix.account_metrics).length === 0) && !loading && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Quality Data Available</h3>
          <p className="text-gray-500 mb-4">
            Run a quality evaluation to generate metrics for your accounts.
          </p>
          <button
            onClick={() => setShowEvaluateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <Play className="w-4 h-4 mr-2" />
            Run First Evaluation
          </button>
        </div>
      )}

      {/* Evaluation Modal */}
      {showEvaluateModal && (
        <EvaluationModal
          domains={getDomains()}
          languagePairs={getLanguagePairs()}
          accounts={matrix?.accounts || {}}
          isEvaluating={isEvaluating}
          progress={evaluationProgress}
          onEvaluate={handleRunEvaluation}
          onClose={() => {
            setShowEvaluateModal(false)
            setEvaluationProgress(null)
          }}
        />
      )}

      {/* Account Detail Modal */}
      {showAccountDetail && (
        <AccountDetailModal
          account={showAccountDetail}
          onReset={(account) => setShowResetConfirm({ open: true, account })}
          onClose={() => setShowAccountDetail(null)}
        />
      )}

      {/* Reset Confirmation */}
      <ConfirmDialog
        isOpen={showResetConfirm.open}
        title="Reset Quality Metrics"
        message={`Are you sure you want to reset all quality metrics for ${showResetConfirm.account?.name}? This action cannot be undone.`}
        confirmText="Reset"
        variant="danger"
        isLoading={isResetting}
        onConfirm={handleResetQuality}
        onClose={() => setShowResetConfirm({ open: false, account: null })}
      />

      {/* Toast notifications */}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  )
}

// Evaluation Modal Component
const EvaluationModal = ({ domains, languagePairs, accounts, isEvaluating, progress, onEvaluate, onClose }) => {
  const [selectedDomains, setSelectedDomains] = useState([...domains])
  const [selectedLanguagePairs, setSelectedLanguagePairs] = useState([])
  const [selectedAccountIds, setSelectedAccountIds] = useState([])
  const [forceReevaluation, setForceReevaluation] = useState(false)
  const [benchmarkSize, setBenchmarkSize] = useState(5)

  // Convert accounts object to array for easier rendering
  const accountsList = Object.values(accounts).sort((a, b) => a.id - b.id)

  const toggleDomain = (domain) => {
    setSelectedDomains(prev =>
      prev.includes(domain)
        ? prev.filter(d => d !== domain)
        : [...prev, domain]
    )
  }

  const toggleAllDomains = () => {
    if (selectedDomains.length === domains.length) {
      setSelectedDomains([])
    } else {
      setSelectedDomains([...domains])
    }
  }

  const toggleAccount = (accountId) => {
    setSelectedAccountIds(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    )
  }

  const toggleAllAccounts = () => {
    if (selectedAccountIds.length === accountsList.length) {
      setSelectedAccountIds([])
    } else {
      setSelectedAccountIds(accountsList.map(acc => acc.id))
    }
  }

  const handleSubmit = () => {
    onEvaluate({
      account_ids: selectedAccountIds.length > 0 ? selectedAccountIds : null,
      domains: selectedDomains.length > 0 ? selectedDomains : null,
      language_pairs: selectedLanguagePairs.length > 0 ? selectedLanguagePairs : null,
      force_reevaluation: forceReevaluation,
      benchmark_size: benchmarkSize
    })
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Play className="w-5 h-5" />
            Run Quality Evaluation
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Evaluate translation quality for selected domains and language pairs
          </p>
        </div>

        <div className="p-6 space-y-4">
          {/* Progress */}
          {progress && (
            <div className={clsx(
              'p-4 rounded-lg',
              progress.status === 'completed' ? 'bg-green-50 border border-green-200' :
              progress.status === 'failed' ? 'bg-red-50 border border-red-200' :
              'bg-blue-50 border border-blue-200'
            )}>
              <div className="flex items-center gap-2">
                {progress.status === 'completed' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : progress.status === 'failed' ? (
                  <XCircle className="w-5 h-5 text-red-600" />
                ) : (
                  <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                )}
                <span className={clsx(
                  'font-medium',
                  progress.status === 'completed' ? 'text-green-800' :
                  progress.status === 'failed' ? 'text-red-800' : 'text-blue-800'
                )}>
                  {progress.message || 'Running evaluation...'}
                </span>
              </div>
              {progress.status === 'completed' && (
                <div className="mt-2 text-sm text-gray-600">
                  <p>Successful: {progress.successful_evaluations}</p>
                  <p>Failed: {progress.failed_evaluations}</p>
                  <p>Total: {progress.total_evaluations}</p>
                </div>
              )}
            </div>
          )}

          {!progress && (
            <>
              {/* Accounts Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Accounts to Evaluate
                  </label>
                  <button
                    type="button"
                    onClick={toggleAllAccounts}
                    className="text-xs text-indigo-600 hover:text-indigo-800"
                  >
                    {selectedAccountIds.length === accountsList.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                {accountsList.length > 0 ? (
                  <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                    {accountsList.map(account => (
                      <label key={account.id} className="flex items-center hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedAccountIds.includes(account.id)}
                          onChange={() => toggleAccount(account.id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          <span className="font-medium">{account.provider}/{account.model}</span>
                          <span className="text-gray-500 ml-1">(#{account.id})</span>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No accounts with metrics found</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to evaluate all active accounts.
                </p>
              </div>

              {/* Domains Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Domains to Evaluate
                  </label>
                  <button
                    type="button"
                    onClick={toggleAllDomains}
                    className="text-xs text-indigo-600 hover:text-indigo-800"
                  >
                    {selectedDomains.length === domains.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {domains.map(domain => (
                    <label key={domain} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedDomains.includes(domain)}
                        onChange={() => toggleDomain(domain)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {domainConfig[domain]?.label || domain}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Language Pairs */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language Pairs (optional)
                </label>
                <select
                  multiple
                  value={selectedLanguagePairs}
                  onChange={(e) => setSelectedLanguagePairs(
                    Array.from(e.target.selectedOptions, option => option.value)
                  )}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500 h-24"
                >
                  {languagePairs.map(lp => (
                    <option key={lp} value={lp}>{lp}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Hold Ctrl/Cmd to select multiple. Leave empty to evaluate all.
                </p>
              </div>

              {/* Options */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="forceReevaluation"
                    checked={forceReevaluation}
                    onChange={(e) => setForceReevaluation(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="forceReevaluation" className="ml-2 text-sm text-gray-700">
                    Force re-evaluation (ignore recent evaluations)
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700">Benchmark size:</label>
                  <input
                    type="number"
                    value={benchmarkSize}
                    onChange={(e) => setBenchmarkSize(parseInt(e.target.value) || 5)}
                    min={1}
                    max={20}
                    className="w-16 rounded-md border-gray-300 shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-xs text-gray-500">samples per evaluation</span>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Note:</p>
                    <ul className="mt-1 list-disc list-inside">
                      <li>Evaluation makes API calls to LLM providers (incurs costs)</li>
                      <li>Running time depends on number of accounts and domains</li>
                      <li>Consider running for specific domains rather than all</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {progress ? 'Close' : 'Cancel'}
          </button>
          {!progress && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isEvaluating || selectedDomains.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center"
            >
              {isEvaluating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Start Evaluation
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Account Detail Modal Component
const AccountDetailModal = ({ account, onReset, onClose }) => {
  const [quality, setQuality] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAccountQuality = async () => {
      try {
        setLoading(true)
        const data = await adminApi.getAccountQuality(account.id)
        setQuality(data)
      } catch (err) {
        console.error('Failed to fetch account quality:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchAccountQuality()
  }, [account.id])

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {account.name}
            </h3>
            <p className="text-sm text-gray-500">Quality Metrics Detail</p>
          </div>
          <button
            onClick={() => onReset(account)}
            className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 hover:text-red-800"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset Metrics
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : quality?.quality_metrics && Object.keys(quality.quality_metrics).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(quality.quality_metrics).map(([langPair, domains]) => (
                <div key={langPair} className="border rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 font-medium text-sm">
                    {langPair}
                  </div>
                  <div className="divide-y">
                    {Object.entries(domains).map(([domain, metrics]) => (
                      <div key={domain} className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={clsx(
                            'inline-block px-2 py-0.5 rounded text-xs font-medium text-white',
                            domainConfig[domain]?.color || 'bg-gray-500'
                          )}>
                            {domainConfig[domain]?.label || domain}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-gray-500">
                            BLEU: <span className="font-medium text-gray-700">{formatScore(metrics.bleu_score)}%</span>
                          </div>
                          <div className="text-gray-500">
                            chrF: <span className="font-medium text-gray-700">{formatScore(metrics.chrf_score)}%</span>
                          </div>
                          <div className="text-gray-500">
                            TER: <span className="font-medium text-gray-700">{formatScore(metrics.ter_score)}%</span>
                          </div>
                          <div className={clsx(
                            'px-2 py-0.5 rounded font-medium',
                            getScoreColor(metrics.composite_score)
                          )}>
                            {formatScore(metrics.composite_score)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No quality metrics available for this account.</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default QualityMatrixPage
